/**
 * OKX P2P Adapter — normalizes all OKX API calls to the domain model.
 *
 * Correct endpoint: GET /v3/c2c/tradingOrders/getMarketplaceAdsPrelogin
 * Response format: { data: { buy: [Ad[]], sell: [Ad[]] } }
 *
 * OKX Ad fields:
 *   id → advNo
 *   price → price (number)
 *   availableAmount → surplusAmount (string → number)
 *   baseCurrency → asset (e.g. "USDT")
 *   quoteMinAmountPerOrder / quoteMaxAmountPerOrder
 *   paymentMethods → tradeMethods (string[])
 *   nickName → advertiser.nickName
 *   completedOrderQuantity → advertiser.monthOrderCount
 *   completedRate → advertiser.monthFinishRate (string "0.98" → number)
 *   creatorType → advertiser.userType ("USER" → "user", "MERCHANT" → "merchant")
 *   minKycLevel → buyerKycLimit
 *   publicUserId → advertiser.userNo
 *   posReviewPercentage → advertiser.positiveRate
 */

import axios from "axios";
import { ExchangeAdapter } from "./types";
import { P2PAd, SearchParams, SearchResult, P2PAdDetail, TradeMethod, PriceSignal } from "./domain";

// ─── OKX P2P API hosts ──────────────────────────────────────────────────
const SEARCH_HOSTS = ["www.okx.com", "okx.com"];

// ─── Correct OKX P2P endpoints ──────────────────────────────────────────
const OKX_P2P_SEARCH_PATH = "/v3/c2c/tradingOrders/getMarketplaceAdsPrelogin";

// CORS proxies that work with OKX
const CORS_PROXIES = [
  "https://freehost-2.lehongthai.vip/cors-anywhere.php",
  "https://host-1.lehongthai.vip/cors-anywhere.php",
];

// ─── Detect OKX promotion zone (boostType: "PAID") ─────────────────────
function isPromotionZone(item: any): boolean {
  return item.boostType === "PAID";
}

// ─── VIP merchant rules (OKX-specific) ──────────────────────────────────
interface VipRule {
  match: (ad: P2PAd) => boolean;
  className: string;
}

const VIP_RULES: VipRule[] = [
  // OKX promotion zone (boostType: "PAID" — GiaoDichNhanh24/7)
  { match: ad => !!ad.promotionZone, className: "!text-white !bg-slate-600 !opacity-100 relative before:absolute before:inset-0 before:border-2 before:border-yellow-400 before:rounded-md before:animate-[pulse_2s_ease-in-out_infinite]" },
];  // OKX — rules will be added when docs are available

// ─── Helper: pick random host ───────────────────────────────────────────
function randomHost<T>(hosts: T[]): T {
  return hosts[Math.floor(Math.random() * hosts.length)];
}

// ─── Helper: detect mobile from User-Agent ──────────────────────────────
function isMobileFromUA(): boolean {
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// ─── Normalize tradeType from OKX side string ───────────────────────────
function normalizeTradeType(raw: string): "BUY" | "SELL" {
  const upper = raw.toUpperCase();
  if (upper === "BUY" || upper === "B") return "BUY";
  return "SELL";
}

// ─── Detect merchant level from OKX badgeInfo.badgeList[0].badgeId ─────
function detectMerchantLevel(item: any): number | undefined {
  const badgeList = item.badgeInfo?.badgeList;
  if (!badgeList || !Array.isArray(badgeList) || badgeList.length === 0) return undefined;
  return Number(badgeList[0].badgeId);
}

// ─── Normalize advertiser type from OKX creatorType ─────────────────────
function normalizeUserType(raw: string): "user" | "merchant" {
  if (raw === "MERCHANT" || raw === "PRO") return "merchant";
  return "user";
}

// ─── Normalize OKX payment methods (string[]) to TradeMethod[] ──────────
function normalizePaymentMethods(methods: string[]): TradeMethod[] {
  if (!methods || !Array.isArray(methods)) return [];
  return methods.map((m) => ({
    payId: null,
    payMethodId: m,
    payType: m,
    payAccount: null,
    payBank: null,
    paySubBank: null,
    identifier: m,
    tradeMethodName: m,
    tradeMethodShortName: m,
    tradeMethodBgColor: "",
    iconUrl: "",
  }));
}

// ─── Normalize an OKX ad item to P2PAd ──────────────────────────────────
function normalizeAd(item: any): P2PAd {
  const completedRate = Number(item.completedRate ?? "0");
  const positiveRate = Number(item.posReviewPercentage ?? "0");

  return {
    advNo: String(item.id ?? ""),
    tradeType: normalizeTradeType(item.side ?? "SELL"),
    asset: (item.baseCurrency ?? "USDT").toUpperCase(),
    price: String(item.price ?? "0"),
    surplusAmount: Number(item.availableAmount ?? item.surplusAmount ?? 0),
    tradableQuantity: String(item.availableAmount ?? ""),
    maxSingleTransAmount: Number(item.quoteMaxAmountPerOrder ?? 0),
    minSingleTransAmount: Number(item.quoteMinAmountPerOrder ?? 0),
    dynamicMaxSingleTransAmount: Number(item.quoteMaxAmountPerOrder ?? 0),
    commissionRate: 0,
    payTimeLimit: Number(item.paymentTimeoutMinutes ?? 15),
    classify: item.classify || undefined,
    takerAdditionalKycRequired: Number(item.minKycLevel ?? 0),
    tradeMethods: normalizePaymentMethods(item.paymentMethods),
    promotionZone: isPromotionZone(item),
    advertiser: {
      userNo: String(item.publicUserId ?? ""),
      nickName: item.nickName ?? "",
      userType: normalizeUserType(item.creatorType ?? "USER"),
      monthFinishRate: completedRate,
      monthOrderCount: Number(item.completedOrderQuantity ?? 0),
      positiveRate: positiveRate,
      activeTimeInSecond: 0,  // OKX listing API không có offline field → giả định online
      vipLevel: item.creatorType === "MERCHANT" ? 1 : 0,
      merchantLevel: detectMerchantLevel(item),
    },
    assetScale: item.baseCurrencyDecimals ?? undefined,
    buyerKycLimit: item.minKycLevel ?? undefined,
  };
}

// ─── Fake ads detection (OKX-specific heuristics) ───────────────────────
function isFakeAds(ad: P2PAd): boolean {
  const dynamicMaxTrade = ad.dynamicMaxSingleTransAmount;
  const percentSuccess = (ad.advertiser.monthFinishRate ?? 0) * 100;
  const minTrade = ad.minSingleTransAmount;

  if (dynamicMaxTrade <= 5_000_000) return true;
  if (percentSuccess < 90) return true;
  if (minTrade >= 50_000_000 && percentSuccess < 97 && dynamicMaxTrade >= 1_000_000_000) return true;
  if (dynamicMaxTrade / minTrade < 3 && (dynamicMaxTrade < 8_000_000 || percentSuccess < 98)) return true;

  return false;
}

// ─── VIP style lookup ───────────────────────────────────────────────────
function getVipStyle(ad: P2PAd, accessLevel: number): string | null {
  if (accessLevel < 1) return null;

  for (const rule of VIP_RULES) {
    if (rule.match(ad)) return rule.className;
  }

  return null;
}

// ─── Required OKX P2P request headers ───────────────────────────────────
function getOkxP2PHeaders(): Record<string, string> {
  return {
    "X-Locale": "en_US",
    "X-Utc": "7",
    "Referer": "https://www.okx.com/p2p-markets",
    "DNT": "1",
    "App-Type": "web",
  };
}

// ══════════════════════════════════════════════════════════════════════
//  OKX Adapter Implementation
// ══════════════════════════════════════════════════════════════════════
export const okxAdapter: ExchangeAdapter = {
  id: "okx",
  displayName: "OKX P2P",
  title: "OKX P2P",
  favicon: "/okx-favicon.svg",

  // ─── searchAds ─────────────────────────────────────────────────────
  async searchAds(params: SearchParams, isMobile?: boolean): Promise<SearchResult> {
    const mobile = isMobile ?? isMobileFromUA();
    const host = randomHost(SEARCH_HOSTS);

    // Map tradeType to OKX side (lowercase)
    const side = params.tradeType === 0 ? "sell" : "buy";

    // Sort: buy → price desc (cheapest first for buyer), sell → price asc (cheapest first for seller)
    const sortType = side === "buy" ? "price_desc" : "price_asc";

    // Build query params per OKX API docs
    const queryParams = new URLSearchParams({
      side,
      cryptoCurrency: params.asset || "USDT",
      fiatCurrency: "VND",
      currentPage: "1",
      numberPerPage: String(params.rows || 15),
      limit: String(params.rows || 15),
      sortType,
      t: Date.now().toString(),
    });

    // Add VND amount filter
    if (params.transAmount) {
      queryParams.set("quoteMinAmountPerOrder", String(params.transAmount));
    }

    // Hide overseas verification ads
    queryParams.set("hideOverseasVerificationAds", "1");

    const targetUrl = `https://${host}${OKX_P2P_SEARCH_PATH}?${queryParams.toString()}`;

    const headers = getOkxP2PHeaders();

    const config = mobile
      ? {
          method: "get" as const,
          url: `${randomHost(CORS_PROXIES)}?url=${encodeURIComponent(targetUrl)}`,
          headers,
        }
      : {
          method: "get" as const,
          url: targetUrl,
          headers,
        };

    const res = await axios(config)
      .then((r) => r.data)
      .catch((err) => err?.response?.data || null);

    // OKX response: { data: { buy: [Ad[]], sell: [Ad[]] } }
    let adsList: any[] = [];
    if (res?.data?.buy && Array.isArray(res.data.buy)) {
      adsList = res.data.buy;
    }
    if (res?.data?.sell && Array.isArray(res.data.sell)) {
      adsList = adsList.concat(res.data.sell);
    }

    const normalized = adsList.map((item: any) => normalizeAd(item));

    return { data: normalized };
  },

  // ─── getAdDetail ─────────────────────────────────────────────────────
  async getAdDetail(advNo: string, isMobile?: boolean): Promise<P2PAdDetail | null> {
    try {
      // OKX doesn't have a dedicated ad detail endpoint.
      // We use the search endpoint and filter by adv ID.
      const mobile = isMobile ?? isMobileFromUA();
      const host = randomHost(SEARCH_HOSTS);

      const side = "sell";
      const queryParams = new URLSearchParams({
        side,
        cryptoCurrency: "USDT",
        fiatCurrency: "VND",
        currentPage: "1",
        numberPerPage: "100",
        limit: "100",
        sortType: "price_asc",
        t: Date.now().toString(),
      });

      const targetUrl = `https://${host}${OKX_P2P_SEARCH_PATH}?${queryParams.toString()}`;
      const config = mobile
        ? {
            method: "get" as const,
            url: `${randomHost(CORS_PROXIES)}?url=${encodeURIComponent(targetUrl)}`,
            headers: getOkxP2PHeaders(),
          }
        : { method: "get" as const, url: targetUrl, headers: getOkxP2PHeaders() };

      const res = await axios(config)
        .then((r) => r.data)
        .catch((err) => err?.response?.data || null);

      if (!res?.data) return null;

      const allAds = [
        ...(res.data.buy || []),
        ...(res.data.sell || []),
      ];
      const found = allAds.find((a: any) => String(a.id) === advNo);
      if (!found) return null;

      const ad = normalizeAd(found);

      return {
        adv: {
          ...ad,
          initAmount: ad.surplusAmount,
          createTime: 0,
          advUpdateTime: 0,
          advStatus: 1,
          priceType: 0,
          priceFloatingRatio: "",
          rateFloatingRatio: "",
          currencyRate: null,
          fiatUnit: "VND",
          fiatScale: 0,
          priceScale: 0,
          fiatSymbol: "",
          assetLogo: "",
          minSingleTransQuantity: "",
          maxSingleTransQuantity: "",
          dynamicMaxSingleTransQuantity: "",
          allowTradeMerchant: 0,
          buyerRegDaysLimit: -1,
          buyerBtcPositionLimit: -1,
          remarks: "",
          abnormalStatusList: [],
          autoReplyMsg: null,
          takerCommissionRate: null,
          minTakerFee: null,
          isSafePayment: false,
          inventoryType: 0,
          offlineReason: "",
          closeReason: "",
          userTradeCountFilterTime: 0,
          userBuyTradeCountMin: 0,
          userBuyTradeCountMax: 0,
          userSellTradeCountMin: 0,
          userSellTradeCountMax: 0,
          userAllTradeCountMin: 0,
          userAllTradeCountMax: 0,
          userTradeCompleteRateFilterTime: null,
          userTradeCompleteCountMin: 0,
          userTradeCompleteRateMin: "",
          userTradeVolumeFilterTime: 0,
          userTradeType: 0,
          userTradeVolumeMin: "",
          userTradeVolumeMax: "",
          userTradeVolumeAsset: null,
          launchCountry: null,
          tradeMethodCommissionRates: [],
        },
        advertiser: {
          ...ad.advertiser,
          orderCount: 0,
          userStatsRet: {
            registerDays: 0,
            firstOrderDays: 0,
            avgReleaseTimeOfLatest30day: 0,
            avgPayTimeOfLatest30day: 0,
            finishRateLatest30day: ad.advertiser.monthFinishRate,
            completedOrderNumOfLatest30day: 0,
            completedBuyOrderNumOfLatest30day: 0,
            completedSellOrderNumOfLatest30day: 0,
            completedOrderTotalBtcAmountOfLatest30day: 0,
            completedOrderNum: 0,
            completedBuyOrderNum: 0,
            completedSellOrderNum: 0,
            completedBuyOrderTotalBtcAmount: 0,
            completedSellOrderTotalBtcAmount: 0,
            completedOrderTotalBtcAmount: 0,
            counterpartyCount: 0,
          },
          userKycRet: undefined,
          businessStatus: null,
          overComplained: null,
          onlineStatus: null,
          registerDays: null,
          firstOrderDays: null,
          chanelInvolved: "",
          userIdentity: ad.advertiser.userType === "merchant" ? "MERCHANT" : "USER",
          userGradeInstanceRet: null,
          proMerchant: ad.advertiser.userType === "merchant",
          badges: [],
          tradeOrderCount: null,
          isBlocked: null,
          alertEnabled: null,
          lastActiveTime: null,
          companyAccountName: "",
          isCompanyAccount: false,
          eqMargin: 0,
          eqMarginUnit: "",
          marginStats: [],
          avatar: null,
          margin: 0,
          marginUnit: "",
          decimalScale: 0,
          advConfirmTime: 0,
          emailVerified: false,
          bindMobile: false,
          kycVerified: false,
          registrationTime: null,
          payMethodCount: ad.tradeMethods.length,
          mobile: null,
          kycType: 0,
          kycStatus: "",
          userGrade: ad.advertiser.vipLevel,
        },
      };
    } catch {
      return null;
    }
  },

  // ─── isFakeAd (OKX heuristics) ────────────────────────────────────
  isFakeAd: (ad: P2PAd) => isFakeAds(ad),

  // ─── getVipStyle (OKX nickname rules) ─────────────────────────────
  getVipStyle: (ad: P2PAd, accessLevel: number) => getVipStyle(ad, accessLevel),

  // ─── getPriceSignals (OKX signals) ────────────────────────────
  getPriceSignals: (ad: P2PAd, accessLevel: number, ctx?: { isKycRequired?: boolean; hasBankPaymentMethod?: boolean; transAmount?: number }) => {
    const signals: PriceSignal[] = [];
    const { dynamicMaxSingleTransAmount, tradeType, classify, minSingleTransAmount, maxSingleTransAmount, tradeMethods } = ad;
    const isKyc = ctx?.isKycRequired ?? (ad.buyerKycLimit !== undefined && ad.buyerKycLimit > 0);
    const hasBank = ctx?.hasBankPaymentMethod ?? (tradeMethods?.some(m => m.payType?.toLowerCase().includes("bank")) ?? true);
    const transAmt = ctx?.transAmount ?? 0;

    // 🐳 cá mập: SELL 500M-1B
    if (accessLevel >= 1 && dynamicMaxSingleTransAmount >= 500_000_000 && dynamicMaxSingleTransAmount < 1_000_000_000 && tradeType === "SELL") {
      signals.push({ emoji: "🐳", label: "Cá mập (bán)", priceClass: "" });
    }

    // 🐋 cá ông: SELL ≥1B (không block)
    if (accessLevel >= 1 && dynamicMaxSingleTransAmount >= 1_000_000_000 && tradeType === "SELL" && classify !== "block") {
      signals.push({ emoji: "🐋", label: "Cá ông (bán)", priceClass: "" });
    }

    // 🐬 cá heo: BUY ≥1B (không block)
    if (accessLevel >= 1 && dynamicMaxSingleTransAmount >= 1_000_000_000 && tradeType === "BUY" && classify !== "block") {
      signals.push({ emoji: "🐬", label: "Cá heo (mua)", priceClass: "" });
    }

    // 💠 block premium: access ≥1, classify block
    if (accessLevel >= 1 && classify === "block") {
      signals.push({ emoji: "💠", label: "Block premium", priceClass: "" });
    }

    // ⚠️ cảnh báo min cao: access 0, SELL, min 99M-700M
    if (accessLevel >= 1 && tradeType === "SELL" && minSingleTransAmount >= 99_000_000 && minSingleTransAmount < 700_000_000) {
      signals.push({ emoji: "⚠️", label: "Min cao (≥99M)", priceClass: "" });
    }

    // 🛡️ KYC required (OKX: bỏ qua vì tất cả ads đều minKycLevel=1)
    // Chỉ kích hoạt khi có ads không yêu cầu KYC cạnh bên
    if (accessLevel >= 1 && isKyc) {
      // OKX: skip — tất cả đều có KYC, signal vô nghĩa
    }

    // 💀 không có BANK
    if (accessLevel >= 1 && !hasBank) {
      signals.push({ emoji: "💀", label: "Không có BANK", priceClass: "" });
    }

    // 🐲 rồng: BUY, min 19M-60M, max >1B, trans ≥100M
    if (accessLevel >= 1 && tradeType === "BUY" && minSingleTransAmount > 19_000_000 && minSingleTransAmount < 60_000_000 && maxSingleTransAmount > 1_000_000_000 && transAmt >= 100_000_000) {
      signals.push({ emoji: "🐲", label: "Rồng (mua signaled)", priceClass: "" });
    }

    return signals;
  },

  // ─── getMerchantColorRules (OKX — badge-based) ────────────────────
  getMerchantColorRules: (ad: P2PAd) => {
    if (ad.advertiser.merchantLevel !== undefined) return "text-cyan-300"; // Diamond / Super / regular merchant
    return null;                                                             // normal user
  },

  // ─── isMerchantPerspective (OKX: merchant perspective) ────────────
  isMerchantPerspective: () => true,

  // ─── getLogo (OKX — badge-based detection) ──────────────────────
  getLogo: (ad: P2PAd) => {
    const level = ad.advertiser.merchantLevel;
    if (level === -1000) return "💎";      // Diamond Merchant
    if (level === -1001) return "👑";      // Super Merchant
    return "🐮";                            // regular merchant or normal user
  },

  // ─── OKX P2P links (no external linking supported) ───────────────
  getAdvertiserLink: () => null,
  getLink: () => null,
};
